import React, { Component } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { State, City } from '../usLocations';
import { readAffiliateRef, clearAffiliateRef } from '../affiliateRef';
import { readLocal, writeLocal, writeSession, storageAvailable, STORAGE_BLOCKED_MESSAGE } from '../safeStorage';
import heic2any from 'heic2any';
import '../static/css/SignUp.css';
import logo from '../static/images/images/a-logo-transperent.png';

const eyeIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const eyeOffIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

const cameraIcon = (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
    </svg>
);

// 🔒 Shared verbatim with iOS and Android this round. Never name the category, never say "nudity".
const PHOTO_REJECTED_TITLE = 'Photo Not Accepted';
const PHOTO_REJECTED_MESSAGE = "This photo can't be used. Nudity and sexual content aren't allowed. Please choose a different one.";

// The API is unversioned, so a-server can start returning an error key this file has never heard of.
// Before this existed, an unrecognised key collapsed into "Something went wrong. Please try again."
// and the real reason was silently dropped. Returns '' when there is nothing worth showing.
const genericServerError = (data) => {
    if (!data || typeof data !== 'object') return '';
    const usable = (value) => {
        if (typeof value !== 'string') return '';
        const trimmed = value.trim();
        // Guard against a proxy's HTML error page or a novel-length body landing in the UI.
        if (!trimmed || trimmed === 'SUCCESS' || trimmed.charAt(0) === '<' || trimmed.length > 300) return '';
        return trimmed;
    };
    // The two deliberate generic channels first (server.js's global handler sends {error}).
    const named = usable(data.error) || usable(data.message);
    if (named) return named;
    // Then ANY keyed string in the body. a-server's failures are flat key -> English-sentence
    // objects, so a key this file has never heard of still carries something the user can act on.
    // Bare-string 500 bodies ("Server Error!" from the route catch-alls, and validation.js's
    // "Something went wrong, please try agian later.") are developer strings — they fail the
    // typeof check above and are deliberately never shown.
    const keys = Object.keys(data);
    for (let i = 0; i < keys.length; i++) {
        const value = usable(data[keys[i]]);
        if (value) return value;
    }
    return '';
};

const months = [
    { label: 'Jan', value: '01' }, { label: 'Feb', value: '02' },
    { label: 'Mar', value: '03' }, { label: 'Apr', value: '04' },
    { label: 'May', value: '05' }, { label: 'Jun', value: '06' },
    { label: 'Jul', value: '07' }, { label: 'Aug', value: '08' },
    { label: 'Sep', value: '09' }, { label: 'Oct', value: '10' },
    { label: 'Nov', value: '11' }, { label: 'Dec', value: '12' },
];

class SignUp extends Component {
    constructor(props) {
        super(props);
        // If the visitor came from the pricing page (/signup?plan=monthly|yearly), remember the plan so
        // that after the free account is created we route them into /manage-subscription to subscribe.
        const planParam = new URLSearchParams(window.location.search).get('plan');
        this.state = {
            plan: (planParam === 'monthly' || planParam === 'yearly') ? planParam : null,
            isLoading: false,
            first_name: '',
            last_name: '',
            email: '',
            password: '',
            confirm_password: '',
            display_name: '',
            city: 'Select City',
            states: 'Select State',
            birth_month: 'Month',
            birth_day: 'Day',
            birth_year: 'Year',
            birthdate: '',
            gender: '',
            image: null,
            image_preview: null,
            showPassword: false,
            showConfirmPassword: false,
            usStates: State.getStatesOfCountry('US'),
            usStateCities: [],
            errors: {},
            generalError: '',
            // Probed once here rather than per render — three storage ops is not free, and the
            // answer cannot change without the reload the message asks for.
            storageBlocked: !storageAvailable(),
            showRejectionModal: false,
            rejectionMessage: '',
            // Bumped to remount the <input type="file"> after a server-rejected photo, so the native
            // input is genuinely empty and re-picking the SAME file still fires onChange.
            imageInputKey: 0,
        };

        this.onChange = this.onChange.bind(this);
        this.onChangeStates = this.onChangeStates.bind(this);
        this.onChangeBirthMonth = this.onChangeBirthMonth.bind(this);
        this.onChangeBirthDay = this.onChangeBirthDay.bind(this);
        this.onChangeBirthYear = this.onChangeBirthYear.bind(this);
        this.onChangeImage = this.onChangeImage.bind(this);
        this.toggleShowPassword = this.toggleShowPassword.bind(this);
        this.toggleShowConfirmPassword = this.toggleShowConfirmPassword.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.closeRejectionModal = this.closeRejectionModal.bind(this);
    }

    onChange(field) {
        return (e) => {
            const next = { generalError: '', errors: { ...this.state.errors, [field]: undefined } };
            next[field] = e.target.value;
            this.setState(next);
        };
    }

    onChangeStates(e) {
        const states = e.target.value;
        this.setState({
            states: states,
            usStateCities: states === 'Select State' ? [] : City.getCitiesOfState('US', states),
            city: 'Select City',
            errors: { ...this.state.errors, state_city: undefined },
            generalError: '',
        });
    }

    pad2 = (n) => String(n).padStart(2, '0');

    daysInMonth = (year, monthValue) => {
        const y = parseInt(year, 10);
        const m = parseInt(monthValue, 10);
        if (!y || !m) return 31;
        return new Date(y, m, 0).getDate();
    };

    buildBirthdateString = (year, monthValue, day) => {
        if (!year || !monthValue || !day) return '';
        return `${String(year)}-${String(monthValue)}-${this.pad2(day)}`;
    };

    is18OrOlder = (yyyy, mm, dd) => {
        const year = parseInt(yyyy, 10);
        const month = parseInt(mm, 10);
        const day = parseInt(dd, 10);
        if (!year || !month || !day) return false;
        const birth = new Date(year, month - 1, day);
        if (birth.getFullYear() !== year || birth.getMonth() !== (month - 1) || birth.getDate() !== day) return false;
        const now = new Date();
        const cutoff = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());
        return birth <= cutoff;
    };

    onChangeBirthMonth(e) {
        const value = e.target.value;
        this.setState((prev) => {
            const maxDay = prev.birth_year !== 'Year' && value !== 'Month'
                ? this.daysInMonth(prev.birth_year, value) : 31;
            const dayInt = parseInt(prev.birth_day, 10);
            const shouldReset = prev.birth_day !== 'Day' && (!dayInt || dayInt > maxDay);
            const nextDay = shouldReset ? 'Day' : prev.birth_day;
            const nextBirthdate = prev.birth_year !== 'Year' && value !== 'Month' && nextDay !== 'Day'
                ? this.buildBirthdateString(prev.birth_year, value, nextDay) : '';
            return { birth_month: value, birth_day: nextDay, birthdate: nextBirthdate, errors: { ...prev.errors, birthdate: undefined }, generalError: '' };
        });
    }

    onChangeBirthDay(e) {
        const value = e.target.value;
        this.setState((prev) => {
            const nextBirthdate = prev.birth_year !== 'Year' && prev.birth_month !== 'Month' && value !== 'Day'
                ? this.buildBirthdateString(prev.birth_year, prev.birth_month, value) : '';
            return { birth_day: value, birthdate: nextBirthdate, errors: { ...prev.errors, birthdate: undefined }, generalError: '' };
        });
    }

    onChangeBirthYear(e) {
        const value = e.target.value;
        this.setState((prev) => {
            const maxDay = value !== 'Year' && prev.birth_month !== 'Month'
                ? this.daysInMonth(value, prev.birth_month) : 31;
            const dayInt = parseInt(prev.birth_day, 10);
            const shouldReset = prev.birth_day !== 'Day' && (!dayInt || dayInt > maxDay);
            const nextDay = shouldReset ? 'Day' : prev.birth_day;
            const nextBirthdate = value !== 'Year' && prev.birth_month !== 'Month' && nextDay !== 'Day'
                ? this.buildBirthdateString(value, prev.birth_month, nextDay) : '';
            return { birth_year: value, birth_day: nextDay, birthdate: nextBirthdate, errors: { ...prev.errors, birthdate: undefined }, generalError: '' };
        });
    }

    async onChangeImage(e) {
        const file = e.target.files[0];
        if (!file) return;
        const isHeic = file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic');
        if (isHeic) {
            this.setState({ image: file, image_preview: 'converting', errors: { ...this.state.errors, image: undefined } });
            try {
                const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.8 });
                // heic2any takes seconds. A server rejection (or another pick) may have replaced or
                // cleared the file while it ran — don't put a preview back over "No file chosen".
                if (this.state.image === file) this.setState({ image_preview: URL.createObjectURL(blob) });
            } catch {
                if (this.state.image === file) this.setState({ image_preview: 'unsupported' });
            }
        } else {
            this.setState({ image: file, image_preview: URL.createObjectURL(file), errors: { ...this.state.errors, image: undefined }, generalError: '' });
        }
    }

    toggleShowPassword() {
        this.setState((prev) => ({ showPassword: !prev.showPassword }));
    }

    toggleShowConfirmPassword() {
        this.setState((prev) => ({ showConfirmPassword: !prev.showConfirmPassword }));
    }

    validLetters = (val) => /^[A-Za-z_ ]+$/.test(val);
    validEmail = (val) => /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(val);
    validPasswordNumber = (p) => /\d/.test(p);
    validPasswordSpecial = (p) => /^(?=.*[^a-zA-Z0-9])[\x21-\x7E]+$/.test(p);
    validAlphaNumeric = (val) => /^[a-zA-Z0-9._]+$/.test(val);

    runValidation() {
        const errors = {};
        const s = this.state;

        if (!s.first_name || s.first_name.length < 2) errors.first_name = "First name must be at least 2 characters.";
        else if (s.first_name.length > 45) errors.first_name = "First name cannot exceed 45 characters.";
        else if (!this.validLetters(s.first_name)) errors.first_name = "First name must contain letters only.";

        if (!s.last_name || s.last_name.length < 2) errors.last_name = "Last name must be at least 2 characters.";
        else if (s.last_name.length > 45) errors.last_name = "Last name cannot exceed 45 characters.";
        else if (!this.validLetters(s.last_name)) errors.last_name = "Last name must contain letters only.";

        // Minimum 2 because /people/search and /people/invite-search both ignore queries shorter than
        // 2 characters — a 1-character display name is unfindable, so the user could never be searched
        // for or invited to an event. Mirrors validateDisplayName in a-server/utils/displayNameRules.js.
        if (!s.display_name) errors.display_name = "Display name is required.";
        else if (s.display_name.length < 2) errors.display_name = "Display name must be at least 2 characters.";
        else if (s.display_name.length > 25) errors.display_name = "Display name cannot exceed 25 characters.";
        else if (!this.validAlphaNumeric(s.display_name)) errors.display_name = "Display name can only contain letters, numbers, underscores, and periods (no spaces).";

        if (!s.email) errors.email = "Email is required.";
        else if (!this.validEmail(s.email)) errors.email = "Invalid email format.";

        if (!s.password) errors.password = "Password is required.";
        else if (s.password.length < 8 || s.password.length > 64) errors.password = "Password must be between 8 and 64 characters.";
        else if (!this.validPasswordNumber(s.password)) errors.password = "Password must contain at least one number.";
        else if (!this.validPasswordSpecial(s.password)) errors.password = "Password must contain at least one special character (any symbol except spaces).";

        if (!s.confirm_password) errors.confirm_password = "Please confirm your password.";
        else if (s.password !== s.confirm_password) errors.confirm_password = "Passwords don't match.";

        if (s.states === 'Select State' || s.city === 'Select City') errors.state_city = "You must select a state and city.";

        if (s.birth_month === 'Month' || s.birth_day === 'Day' || s.birth_year === 'Year') {
            errors.birthdate = "Birthdate is required.";
        } else if (!this.is18OrOlder(s.birth_year, s.birth_month, s.birth_day)) {
            errors.birthdate = "You must be 18 or older to sign up.";
        }

        if (!s.gender || s.gender === 'Select Gender') errors.gender = "Gender is required.";
        if (!s.image) errors.image = "Profile photo is required.";

        return errors;
    }

    async onSubmit(e) {
        e.preventDefault();
        if (this.state.isLoading) return;

        // 🔴 STOP BEFORE THE ACCOUNT EXISTS. With storage blocked the JWT cannot be persisted,
        // so a completed signup leaves them with an account they cannot reach AND AN EMAIL ADDRESS
        // THAT IS NOW TAKEN — retrying after fixing their browser returns email_exists_error.
        // Checked before validation and before any network call, so no POST is issued.
        if (this.state.storageBlocked) return;

        const errors = this.runValidation();
        if (Object.keys(errors).length > 0) {
            this.setState({ errors, generalError: '' });
            return;
        }

        const birthdateString = this.buildBirthdateString(this.state.birth_year, this.state.birth_month, this.pad2(this.state.birth_day));

        const formData = new FormData();
        formData.append('first_name', this.state.first_name.trim());
        formData.append('last_name', this.state.last_name.trim());
        formData.append('email', this.state.email.trim());
        formData.append('password', this.state.password);
        formData.append('confirm_password', this.state.confirm_password);
        formData.append('city', this.state.city);
        formData.append('states', this.state.states);
        formData.append('display_name', this.state.display_name.trim());
        formData.append('birthdate', birthdateString);
        formData.append('gender', this.state.gender);
        formData.append('image', this.state.image);

        // Attach captured affiliate ref (set by AffiliateLanding when the visitor came in via /<displayName>).
        // readAffiliateRef() enforces the 365-day window and clears an expired or malformed value;
        // null means no ref goes on the request. It never throws, so no try/catch is needed here.
        const affiliateRef = readAffiliateRef();
        if (affiliateRef) formData.append('ref', affiliateRef);

        this.setState({ isLoading: true, errors: {}, generalError: '' });

        try {
            const response = await axios.post(
                process.env.REACT_APP_SERVER_API + '/auth/web-sign-up',
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            const { token, user, upcoming_event } = response.data.data;
            writeLocal('token', token);
            writeLocal('user', JSON.stringify(user));
            writeLocal('upcoming_event', JSON.stringify(upcoming_event));
            clearAffiliateRef();
            // Paid funnel: account is created (and logged in) — send them to subscribe with their chosen
            // plan pre-selected. Free signups (no plan) land on the success page, which is shown ONCE
            // (the from_signup signal is consumed there so a refresh/return goes to /home).
            if (this.state.plan) {
                window.location.href = `/manage-subscription?plan=${this.state.plan}`;
            } else {
                writeSession('from_signup', '1');
                window.location.href = '/signup-success';
            }
        } catch (err) {
            const data = (err.response && err.response.data) || {};
            const fieldErrors = {};
            if (data.email_exists_error) fieldErrors.email = data.email_exists_error;
            if (data.display_name_exists_error) fieldErrors.display_name = data.display_name_exists_error;
            // Validation middleware errors are flat keys like first_name_required, password_min, etc.
            if (data.first_name_required || data.first_name_min || data.first_name_max || data.first_name_valid) {
                fieldErrors.first_name = data.first_name_required || data.first_name_min || data.first_name_max || data.first_name_valid;
            }
            if (data.last_name_required || data.last_name_min || data.last_name_max || data.last_name_valid) {
                fieldErrors.last_name = data.last_name_required || data.last_name_min || data.last_name_max || data.last_name_valid;
            }
            if (data.password_required || data.password_min || data.password_number || data.password_special_character) {
                fieldErrors.password = data.password_required || data.password_min || data.password_number || data.password_special_character;
            }
            if (data.confirm_password_required || data.confirm_password_match) {
                fieldErrors.confirm_password = data.confirm_password_required || data.confirm_password_match;
            }
            if (data.display_name_required || data.display_name_error || data.display_name_max_error) {
                fieldErrors.display_name = data.display_name_required || data.display_name_error || data.display_name_max_error;
            }
            // Photo moderation (a-server returns 400 {image_rejected_error}). Prefer the server's
            // wording, fall back to the shared copy when it is absent — same rule as iOS and Android.
            const photoRejected = typeof data.image_rejected_error !== 'undefined';
            const rejectionMessage = photoRejected
                ? (genericServerError({ error: data.image_rejected_error }) || PHOTO_REJECTED_MESSAGE)
                : '';
            if (photoRejected) fieldErrors.image = rejectionMessage;
            const generalError = Object.keys(fieldErrors).length === 0
                ? (genericServerError(data) || 'Something went wrong. Please try again.')
                : '';
            this.setState((prev) => ({
                isLoading: false,
                errors: fieldErrors,
                generalError,
                showRejectionModal: photoRejected,
                rejectionMessage: photoRejected ? rejectionMessage : prev.rejectionMessage,
                // Drop the rejected file so the form cannot resubmit it, and remount the file input
                // so the user is genuinely prompted to choose another.
                image: photoRejected ? null : prev.image,
                image_preview: photoRejected ? null : prev.image_preview,
                imageInputKey: photoRejected ? prev.imageInputKey + 1 : prev.imageInputKey,
            }));
        }
    }

    renderBirthYearOptions() {
        const nowYear = new Date().getFullYear();
        const maxYear = nowYear - 18;
        const start = nowYear - 100;
        const out = [];
        for (let y = maxYear; y >= start; y--) out.push(<option value={String(y)} key={y}>{String(y)}</option>);
        return out;
    }

    renderBirthDayOptions() {
        const { birth_year, birth_month } = this.state;
        let max = 31;
        if (birth_year !== 'Year' && birth_month !== 'Month') {
            max = this.daysInMonth(birth_year, birth_month);
        }
        const out = [];
        for (let d = 1; d <= max; d++) out.push(<option value={String(d)} key={d}>{String(d)}</option>);
        return out;
    }

    renderImageBox() {
        const { image_preview, image } = this.state;
        let inner;
        if (image_preview === 'converting') {
            inner = <div className="signup-image-spinner" />;
        } else if (image_preview === 'unsupported') {
            inner = <span className="signup-image-unsupported">Preview not available</span>;
        } else if (image_preview) {
            inner = <img src={image_preview} alt="" className="signup-image-preview-img" />;
        } else {
            inner = <span className="signup-image-icon">{cameraIcon}</span>;
        }
        return (
            <div className="signup-image-section">
                <p className="signup-image-label">Profile Photo</p>
                <p className="signup-image-hint">A photo of you, your favorite anime character, a GIF — anything that represents you.</p>
                <div className="signup-image-box">{inner}</div>
                <p className="signup-image-filename">{image ? image.name : 'No file chosen'}</p>
                <label className="signup-image-button">
                    <span>Choose file</span>
                    <input key={this.state.imageInputKey} type="file" accept="image/*" onChange={this.onChangeImage} />
                </label>
                {this.state.errors.image && <p className="signup-field-error signup-image-error">{this.state.errors.image}</p>}
            </div>
        );
    }

    renderFieldError(field) {
        const msg = this.state.errors[field];
        if (!msg) return null;
        return <p className="signup-field-error">{msg}</p>;
    }

    closeRejectionModal() {
        // Only the pop-up closes — the inline error under the photo box stays, so the reason survives.
        this.setState({ showRejectionModal: false });
    }

    // Hand-rolled, like every other modal here: PauseAccount.js, DeleteAccount.js and
    // ManageSubscription.js each carry their own copy under their own class prefix. There is no
    // shared modal component in a-client and this round deliberately does not add one.
    renderRejectionModal() {
        return (
            <div
                className="signup-modal-overlay"
                role="dialog"
                aria-modal="true"
                aria-labelledby="signup-modal-title"
            >
                <div className="signup-modal-card">
                    <h3 id="signup-modal-title" className="signup-modal-title" style={{fontFamily: "'Chelsea Market', cursive"}}>{PHOTO_REJECTED_TITLE}</h3>
                    <p className="signup-modal-message">{this.state.rejectionMessage || PHOTO_REJECTED_MESSAGE}</p>
                    <div className="signup-modal-actions">
                        <button
                            type="button"
                            className="signup-modal-dismiss"
                            onClick={this.closeRejectionModal}
                            autoFocus
                        >
                            Got it
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        // Already signed in? They have an account — no need for the signup form. Send them home.
        if (readLocal('token') && readLocal('user')) {
            return <Navigate to="/home" replace />;
        }
        const { isLoading, showPassword, showConfirmPassword, generalError } = this.state;
        return (
            <div className="signup-container">
                <div className="animates-logo-container">
                    <Link to="/"><img className="animates-logo hover" style={{width: "125px"}} src={logo} alt="" /></Link>
                    <div className="welcomecs-header-auth-links">
                        <Link to="/signin" className="welcomecs-header-auth-link">Sign In</Link>
                    </div>
                </div>
                <h2 className="color-1 signup-title" style={{fontFamily: "'Chelsea Market', cursive"}}><u>Sign Up</u></h2>
                <p className="signup-attention"><strong>Heads up:</strong> once your profile is created, you can't change your first name, last name, or date of birth. You must be 18+ to sign up. Last name stays private.</p>
                {this.state.storageBlocked && <p className="signup-error">{STORAGE_BLOCKED_MESSAGE}</p>}
                {generalError && <p className="signup-error">{generalError}</p>}

                <form className="signup-form" onSubmit={this.onSubmit}>

                    {this.renderImageBox()}

                    <div className="signup-form-row">
                        <div className="signup-form-cell">
                            <label className="signup-form-label" htmlFor="signup-first">First name</label>
                            <input id="signup-first" type="text" className="signup-form-input" value={this.state.first_name} onChange={this.onChange('first_name')} autoComplete="given-name" maxLength={45} />
                            {this.renderFieldError('first_name')}
                        </div>
                        <div className="signup-form-cell">
                            <label className="signup-form-label" htmlFor="signup-last">Last name</label>
                            <input id="signup-last" type="text" className="signup-form-input" value={this.state.last_name} onChange={this.onChange('last_name')} autoComplete="family-name" maxLength={45} />
                            {this.renderFieldError('last_name')}
                        </div>
                    </div>

                    <label className="signup-form-label" htmlFor="signup-display">Display name</label>
                    <input id="signup-display" type="text" className="signup-form-input" value={this.state.display_name} onChange={this.onChange('display_name')} autoComplete="username" maxLength={25} />
                    <p className="signup-form-hint">Letters, numbers, underscores, and periods only — up to 25 characters.</p>
                    {this.renderFieldError('display_name')}

                    <label className="signup-form-label" htmlFor="signup-email">Email</label>
                    <input id="signup-email" type="email" className="signup-form-input" value={this.state.email} onChange={this.onChange('email')} autoComplete="email" />
                    {this.renderFieldError('email')}

                    <label className="signup-form-label" htmlFor="signup-password">Password</label>
                    <div className="signup-form-input-wrapper">
                        <input id="signup-password" type={showPassword ? 'text' : 'password'} className="signup-form-input" value={this.state.password} onChange={this.onChange('password')} autoComplete="new-password" />
                        <button type="button" className="signup-form-toggle-password" onClick={this.toggleShowPassword} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                            {showPassword ? eyeIcon : eyeOffIcon}
                        </button>
                    </div>
                    <p className="signup-form-hint">8–64 characters, at least one number and one special character (any symbol except spaces).</p>
                    {this.renderFieldError('password')}

                    <label className="signup-form-label" htmlFor="signup-confirm-password">Confirm password</label>
                    <div className="signup-form-input-wrapper">
                        <input id="signup-confirm-password" type={showConfirmPassword ? 'text' : 'password'} className="signup-form-input" value={this.state.confirm_password} onChange={this.onChange('confirm_password')} autoComplete="new-password" />
                        <button type="button" className="signup-form-toggle-password" onClick={this.toggleShowConfirmPassword} aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                            {showConfirmPassword ? eyeIcon : eyeOffIcon}
                        </button>
                    </div>
                    {this.renderFieldError('confirm_password')}

                    <label className="signup-form-label" htmlFor="signup-state">State &amp; city</label>
                    <div className="signup-form-row">
                        <div className="signup-form-cell">
                            <select id="signup-state" className="signup-form-input signup-form-select" value={this.state.states} onChange={this.onChangeStates}>
                                <option>Select State</option>
                                {this.state.usStates.map(st => <option key={st.isoCode} value={st.isoCode}>{st.isoCode}</option>)}
                            </select>
                        </div>
                        <div className="signup-form-cell">
                            <select className="signup-form-input signup-form-select" value={this.state.city} onChange={this.onChange('city')} disabled={this.state.states === 'Select State'}>
                                <option>{this.state.states === 'Select State' ? 'Select State First' : 'Select City'}</option>
                                {this.state.usStateCities.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                            </select>
                        </div>
                    </div>
                    {this.renderFieldError('state_city')}

                    <label className="signup-form-label">Birthdate</label>
                    <div className="signup-form-row signup-form-row-three">
                        <div className="signup-form-cell">
                            <select className="signup-form-input signup-form-select" value={this.state.birth_month} onChange={this.onChangeBirthMonth}>
                                <option>Month</option>
                                {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </div>
                        <div className="signup-form-cell">
                            <select className="signup-form-input signup-form-select" value={this.state.birth_day} onChange={this.onChangeBirthDay} disabled={this.state.birth_month === 'Month'}>
                                <option>Day</option>
                                {this.renderBirthDayOptions()}
                            </select>
                        </div>
                        <div className="signup-form-cell">
                            <select className="signup-form-input signup-form-select" value={this.state.birth_year} onChange={this.onChangeBirthYear}>
                                <option>Year</option>
                                {this.renderBirthYearOptions()}
                            </select>
                        </div>
                    </div>
                    {this.renderFieldError('birthdate')}

                    <label className="signup-form-label" htmlFor="signup-gender">Gender</label>
                    <select id="signup-gender" className="signup-form-input signup-form-select" value={this.state.gender} onChange={this.onChange('gender')}>
                        <option value="">Select Gender</option>
                        <option value="Man">Man</option>
                        <option value="Woman">Woman</option>
                        <option value="Nonbinary">Nonbinary</option>
                        <option value="Prefer not to say">Prefer not to say</option>
                    </select>
                    {this.renderFieldError('gender')}

                    <button type="submit" className="signup-form-submit" disabled={isLoading}>
                        {isLoading ? 'Creating account…' : 'Create Account'}
                    </button>
                </form>
                <div className="signup-signin-prompt">
                    <span>Already have an account?</span>
                    <Link to="/signin" className="signup-signin-prompt-link">Sign In</Link>
                </div>
                <p className="signup-footer">© 2026 AniMates. All rights reserved.</p>
                {this.state.showRejectionModal && this.renderRejectionModal()}
            </div>
        );
    }
}

export default SignUp;
